import * as React from "react";
import { format, subDays, isFuture, startOfDay } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DatePickerWithRangeProps
  extends React.HTMLAttributes<HTMLDivElement> {
  date?: DateRange;
  onDateChange?: (date: DateRange | undefined) => void;
  placeholder?: string;
  numberOfMonths?: number;
  initialDateFrom?: Date;
  initialDateTo?: Date;
  align?: "start" | "center" | "end";
}

// Helper function to clamp a date to today if it's in the future
const clampToToday = (date: Date | undefined): Date | undefined => {
  if (!date) return undefined;
  const today = startOfDay(new Date());
  const dateToCheck = startOfDay(date);
  return isFuture(dateToCheck) ? today : date;
};

// Helper function to validate and clamp a date range
const validateDateRange = (range: DateRange | undefined): DateRange | undefined => {
  if (!range) return undefined;
  
  const today = startOfDay(new Date());
  const from = range.from ? clampToToday(range.from) : undefined;
  const to = range.to ? clampToToday(range.to) : undefined;
  
  // If both dates exist and 'to' is before 'from', swap them
  if (from && to && to < from) {
    return { from: to, to: from };
  }
  
  return { from, to };
};

export function DatePickerWithRange({
  className,
  date: controlledDate,
  onDateChange,
  placeholder = "Pick a date",
  numberOfMonths = 2,
  initialDateFrom = subDays(new Date(), 6),
  initialDateTo = new Date(),
  align = "start",
  ...props
}: DatePickerWithRangeProps) {
  // Validate initial dates to ensure they're not in the future
  const validatedInitialFrom = clampToToday(initialDateFrom) ?? subDays(new Date(), 6);
  const validatedInitialTo = clampToToday(initialDateTo) ?? new Date();
  
  const [internalDate, setInternalDate] = React.useState<DateRange | undefined>(
    {
      from: validatedInitialFrom,
      to: validatedInitialTo,
    }
  );

  const date = controlledDate ?? internalDate;
  
  // Wrapper function to validate dates before setting them
  const handleDateChange = React.useCallback((newDate: DateRange | undefined) => {
    const validatedDate = validateDateRange(newDate);
    if (onDateChange) {
      onDateChange(validatedDate);
    } else {
      setInternalDate(validatedDate);
    }
  }, [onDateChange]);
  
  // Always use the validated handler to ensure no future dates can be set
  const setDate = handleDateChange;
  const presets = [
    {
      label: "Today",
      getValue: () => ({
        from: new Date(),
        to: new Date(),
      }),
    },
    {
      label: "Yesterday",
      getValue: () => ({
        from: subDays(new Date(), 1),
        to: subDays(new Date(), 1),
      }),
    },
    {
      label: "Last 7 days",
      getValue: () => ({
        from: subDays(new Date(), 6),
        to: new Date(),
      }),
    },
    {
      label: "Last 30 days",
      getValue: () => ({
        from: subDays(new Date(), 29),
        to: new Date(),
      }),
    },
    {
      label: "Last 60 days",
      getValue: () => ({
        from: subDays(new Date(), 59),
        to: new Date(),
      }),
    },
  ];
  return (
    <div className={cn("grid gap-2", className)} {...props}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            size="sm"
            className={cn(
              "w-max-fit justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 flex" align={align}>
          <div className="border-r border-border p-3 space-y-3">
            {presets.map((preset) => {
              // Check if this preset is currently selected
              const isSelected = (() => {
                const presetRange = preset.getValue();
                return (
                  date?.from?.toDateString() ===
                    presetRange.from.toDateString() &&
                  date?.to?.toDateString() === presetRange.to.toDateString()
                );
              })();

              return (
                <Button
                  key={preset.label}
                  variant="ghost"
                  className={cn(
                    "justify-start font-normal w-[10rem] flex",
                    isSelected &&
                      "bg-primary text-primary-foreground hover:bg-primary"
                  )}
                  onClick={() => {
                    const newRange = preset.getValue();
                    // Validate the preset range to ensure no future dates
                    const validatedRange = validateDateRange(newRange);
                    setDate(validatedRange);
                  }}
                >
                  {preset.label}
                </Button>
              );
            })}
          </div>
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={(selectedRange) => {
              // Validate the selected range to ensure no future dates
              const validatedRange = validateDateRange(selectedRange);
              setDate(validatedRange);
            }}
            numberOfMonths={numberOfMonths}
            disabled={(date) => {
              if (!date) return false;
              return isFuture(date);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
